import { useMemo, useState } from 'react';
import ChannelRow, { ChannelItemStatic } from './ChannelRow';
import ChannelContextMenu from './ChannelContextMenu';
import { useUserLayout } from '../hooks/useUserLayout';
import { useAuthStore } from '../stores';
import { sortServerChannels } from '../lib/channelListModel';
import type { Task } from '../lib/api';
import type { Channel } from '../types';
import styles from './ChannelRail.module.css';

const MAX_ACTIVE_TASKS_PER_CHANNEL = 3;

interface Props {
  channels: Channel[];
  currentChannelId: string | null;
  activeTasks: Task[];
  activeThreadId?: string;
  onSelectChannel: (channelId: string) => void;
  onSelectTask: (task: Task) => void;
  onShowAllTasks: (channelId: string) => void;
}

export default function ChannelList({
  channels,
  currentChannelId,
  activeTasks,
  activeThreadId,
  onSelectChannel,
  onSelectTask,
  onShowAllTasks,
}: Props) {
  const currentUser = useAuthStore(s => s.currentUser);
  const { pinChannel, unpinChannel, isPinned } = useUserLayout();
  const [pinMenu, setPinMenu] = useState<{ x: number; y: number; channelId: string } | null>(null);

  const { memberChannels, publicChannels } = useMemo(() => {
    const member: Channel[] = [];
    const publicReadOnly: Channel[] = [];
    for (const channel of channels) {
      if (channel.is_member === false) publicReadOnly.push(channel);
      else member.push(channel);
    }
    return { memberChannels: member, publicChannels: publicReadOnly };
  }, [channels]);

  const sortedMemberChannels = useMemo(
    () => sortServerChannels(memberChannels, channelId => Boolean(currentUser && isPinned(channelId))),
    [currentUser, isPinned, memberChannels],
  );
  const sortedPublicChannels = useMemo(
    () => sortServerChannels(publicChannels, () => false),
    [publicChannels],
  );
  const activeTasksByChannel = useMemo(() => {
    const byChannel = new Map<string, Task[]>();
    for (const task of activeTasks) {
      const tasks = byChannel.get(task.channel_id) ?? [];
      tasks.push(task);
      byChannel.set(task.channel_id, tasks);
    }
    for (const tasks of byChannel.values()) {
      tasks.sort((left, right) => right.updated_at - left.updated_at || left.id.localeCompare(right.id));
    }
    return byChannel;
  }, [activeTasks]);

  return (
    <div className={`channel-list ${styles.list}`} data-kind="channel" data-testid="project-channel-list">
      {sortedMemberChannels.map(channel => {
        const tasks = activeTasksByChannel.get(channel.id) ?? [];
        const visibleTasks = tasks.slice(0, MAX_ACTIVE_TASKS_PER_CHANNEL);
        return (
          <div className="channel-list-entry" key={channel.id}>
            <ChannelRow
              channel={channel}
              active={channel.id === currentChannelId}
              onClick={() => onSelectChannel(channel.id)}
              pinned={isPinned(channel.id)}
              onContextMenu={event => {
                event.preventDefault();
                setPinMenu({ x: event.clientX, y: event.clientY, channelId: channel.id });
              }}
            />
            {visibleTasks.length > 0 && (
              <div className={styles.activeTasks} aria-label={`Active tasks in ${channel.name}`}>
                {visibleTasks.map(task => (
                  <button
                    className={`${styles.task} ${task.thread_id === activeThreadId ? styles.taskActive : ''}`}
                    data-active-task="true"
                    data-task-id={task.id}
                    key={task.id}
                    type="button"
                    onClick={() => onSelectTask(task)}
                    title={`#${task.seq} ${task.title}`}
                  >
                    <span className={`${styles.taskStatus} ${task.status === 'in_progress' ? styles.taskStatusInProgress : styles.taskStatusInReview}`} aria-label={task.status.replace('_', ' ')} />
                    <span className={styles.taskSeq}>#{task.seq}</span>
                    <span className={styles.taskTitle}>{task.title}</span>
                  </button>
                ))}
                {tasks.length > MAX_ACTIVE_TASKS_PER_CHANNEL && (
                  <button className={styles.overflow} data-active-task-overflow="true" type="button" onClick={() => onShowAllTasks(channel.id)}>
                    +{tasks.length - MAX_ACTIVE_TASKS_PER_CHANNEL} active tasks
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {sortedPublicChannels.length > 0 && (
        <>
          <div className={styles.groupLabel}>Public channels</div>
          {sortedPublicChannels.map(channel => (
            <ChannelItemStatic key={channel.id} channel={channel} active={channel.id === currentChannelId} onClick={() => onSelectChannel(channel.id)} />
          ))}
        </>
      )}

      {channels.length === 0 && <div className="sidebar-empty">No channels</div>}
      {pinMenu && (
        <ChannelContextMenu
          x={pinMenu.x}
          y={pinMenu.y}
          pinned={isPinned(pinMenu.channelId)}
          onPin={() => pinChannel(pinMenu.channelId)}
          onUnpin={() => unpinChannel(pinMenu.channelId)}
          onClose={() => setPinMenu(null)}
        />
      )}
    </div>
  );
}
